import { useEffect, useState } from 'react';
import { useList, useOne } from '@refinedev/core';
import { Check, ChevronsUpDown, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getBookTitle, type Book } from '../types';

interface BookSelectorProps {
  value: number | null;
  onChange: (id: number | null) => void;
  /** Вызывается, когда книга выбрана — передаёт её заголовок для автозаполнения. */
  onTitleResolved?: (title: string | null) => void;
  disabled?: boolean;
}

/**
 * Autocomplete-селектор книги из библиотеки. Поиск среди опубликованных книг.
 */
export function BookSelector({ value, onChange, onTitleResolved, disabled }: BookSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const listResult = useList<Book>({
    resource: 'books',
    pagination: { currentPage: 1, pageSize: 50 },
    // НЕ фильтруем по isPublished — нужно разрешить прикреплять к уроку
    // и ещё не опубликованные книги (черновики). В селекторе их помечаем
    // бейджем «Черновик».
    filters: search
      ? [{ field: 'search', operator: 'contains' as const, value: search }]
      : [],
  });

  const books = listResult.result.data ?? [];
  const selectedFromList =
    value !== null ? books.find((b) => b.id === value) ?? null : null;

  // Если выбранной книги нет в списке — подгружаем её отдельно
  const oneResult = useOne<Book>({
    resource: 'books',
    id: value ?? '',
    queryOptions: { enabled: value !== null && !selectedFromList },
  });

  const selected = selectedFromList ?? oneResult.result ?? null;

  useEffect(() => {
    if (selected && onTitleResolved) {
      onTitleResolved(getBookTitle(selected));
    }
  }, [selected, onTitleResolved]);

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="truncate">
              {selected ? getBookTitle(selected) : 'Выберите книгу...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Поиск книги..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>Книги не найдены</CommandEmpty>
              <CommandGroup>
                {books.map((book) => (
                  <CommandItem
                    key={book.id}
                    value={String(book.id)}
                    onSelect={() => {
                      onChange(book.id);
                      onTitleResolved?.(getBookTitle(book));
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === book.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex flex-1 items-center gap-2">
                      <span className="truncate">{getBookTitle(book)}</span>
                      <Badge variant="outline" className="text-xs">{book.format}</Badge>
                      {!book.isPublished && (
                        <Badge variant="secondary" className="text-xs">
                          Черновик
                        </Badge>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
          {selected.coverFileUrl ? (
            <img
              src={selected.coverFileUrl}
              alt=""
              className="h-12 w-9 rounded object-cover"
            />
          ) : (
            <div className="flex h-12 w-9 items-center justify-center rounded bg-muted">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{getBookTitle(selected)}</p>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{selected.format}</Badge>
              {!selected.isPublished && (
                <Badge variant="secondary" className="text-xs">
                  Черновик
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">{selected.languageCode}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
